/**
 * 밝게 / 어둡게 / 기기 설정 따라가기.
 *
 * ⚠️ **실제로 칠하는 일은 `index.html`의 인라인 스크립트가 먼저 한다.**
 * 여기서 처음 칠하면 첫 프레임이 기기 색으로 그려졌다가 바뀐다 — 화면이
 * 한 번 번쩍인다. 이 모듈은 **고르고 바꾸는 쪽**만 맡는다.
 *
 * 계정이 아니라 **브라우저에 남긴다.** 테마는 지금 보는 기기의 사정이다 —
 * 밤에 폰에서 어둡게 보는 사람이 낮에 노트북에서도 어두울 이유가 없다.
 * 언어를 계정에 안 적어 둔 것과 같은 이유다.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'lifetimeline.theme';

/** 주소창·상태바 색. 화면과 다르면 브라우저 껍데기만 따로 떠 보인다 */
const BAR_COLOR: Record<'light' | 'dark', string> = {
  light: '#ffffff',
  dark: '#0d0d0d',
};

export function readTheme(): ThemeChoice {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'light' || saved === 'dark' ? saved : 'system';
  } catch {
    /* 사생활 보호 모드에서는 읽기부터 막힌다 — 기기 설정으로 둔다 */
    return 'system';
  }
}

/** 고른 값(`system` 포함)을 **실제로 칠할 색**으로 푼다 */
export function resolveTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') {
    return choice;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(choice: ThemeChoice): void {
  const resolved = resolveTheme(choice);
  document.documentElement.dataset['theme'] = resolved;
  const bar = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (bar !== null) {
    bar.content = BAR_COLOR[resolved];
  }
}

export function writeTheme(choice: ThemeChoice): void {
  try {
    if (choice === 'system') {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, choice);
    }
  } catch {
    /* 못 적어도 이번 방문에는 적용된다 */
  }
  applyTheme(choice);
}

/**
 * 기기 설정이 바뀌면 따라간다 — **`system`일 때만.**
 *
 * 폰이 밤에 저절로 어두워지는 경우다. 직접 고른 사람은 기기가 바뀌어도
 * 그대로 둬야 한다. 안 그러면 고른 것이 조용히 뒤집힌다.
 */
export function watchSystemTheme(getChoice: () => ThemeChoice): () => void {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (getChoice() === 'system') {
      applyTheme('system');
    }
  };
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}
